import React from "react";
import { Link } from "react-router-dom";
import { IdCard, Zap, Upload, Users, FileSpreadsheet, Download, ArrowRight, CheckCircle, Phone, Mail } from "lucide-react";

const cardCls = "bg-white brutal-border brutal-shadow p-8";
const btnPrimary = "inline-flex items-center gap-2 bg-[#002FA7] text-white font-bold uppercase tracking-wider px-6 py-4 brutal-border brutal-shadow brutal-hover text-sm";
const btnSecondary = "inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-wider px-6 py-4 brutal-border brutal-shadow brutal-hover text-sm";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#F3F4F6]">
      {/* NAV */}
      <nav className="brutal-border border-t-0 border-l-0 border-r-0 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <div className="w-9 h-9 bg-black flex items-center justify-center">
              <IdCard className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-black text-xl tracking-tighter">DIGITAL CARD</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="font-mono text-xs uppercase tracking-wider font-bold hover:underline" data-testid="nav-login">Sign In</Link>
            <Link to="/register" className={btnPrimary} data-testid="nav-register">
              Get Started <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 py-20 md:py-28">
        <div className="grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <div className="inline-block bg-black text-white px-3 py-1 font-mono text-xs uppercase tracking-widest mb-6">
              // Bulk ID Cards, Zero Hassle
            </div>
            <h1 className="font-display font-black text-5xl md:text-7xl leading-[0.9] tracking-tighter mb-6">
              ID cards for<br/>
              <span className="text-[#002FA7]">schools, NGOs</span><br/>
              & businesses.
            </h1>
            <p className="text-lg text-gray-700 max-w-xl mb-8 leading-relaxed">
              Upload your blank template. Drop text and photos exactly where you want them. Generate 1 or 1,000 HD ID cards. Download as PNG or PDF.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/register" className={btnPrimary} data-testid="hero-cta-primary">
                Start Free — 1 ID Card
              </Link>
              <Link to="/login" className={btnSecondary} data-testid="hero-cta-secondary">
                I have an account
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-6 font-mono text-xs uppercase tracking-wider text-gray-600">
              <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#22C55E]"/>No credit card</span>
              <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#22C55E]"/>HD output</span>
              <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#22C55E]"/>₹10 per card</span>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="relative">
              <div className="absolute -inset-2 bg-[#002FA7]"></div>
              <img
                src="https://images.unsplash.com/photo-1671376354112-6de3d08b97af"
                alt="ID Card sample"
                className="relative w-full h-[420px] object-cover brutal-border"
                data-testid="hero-image"
              />
              <div className="absolute -bottom-4 -right-4 bg-black text-white px-4 py-2 font-mono text-xs uppercase tracking-wider">
                / OUTPUT.PDF
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-t-2 border-black bg-white">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="mb-12">
            <div className="font-mono text-xs uppercase tracking-widest text-[#002FA7] font-bold mb-2">// Workflow</div>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Four steps. That's it.</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: Upload, title: "Upload template", desc: "PNG / JPG blank card design.", n: "01" },
              { icon: IdCard, title: "Place fields", desc: "Drag name, designation, photo etc.", n: "02" },
              { icon: FileSpreadsheet, title: "Add data", desc: "Single form or Excel bulk.", n: "03" },
              { icon: Download, title: "Download HD", desc: "PNG + PDF, ready to print.", n: "04" },
            ].map((s, i) => (
              <div key={s.n} className={cardCls} data-testid={`how-step-${i}`}>
                <div className="flex items-center justify-between mb-4">
                  <span className="font-mono text-xs font-bold text-gray-500">{s.n}</span>
                  <s.icon className="w-6 h-6" strokeWidth={2}/>
                </div>
                <h3 className="font-display font-bold text-xl mb-2">{s.title}</h3>
                <p className="text-sm text-gray-700">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO */}
      <section className="border-t-2 border-black">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="mb-12">
            <div className="font-mono text-xs uppercase tracking-widest text-[#002FA7] font-bold mb-2">// Made For</div>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Vendors who print in bulk.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Users, title: "Schools & Colleges", desc: "Student & staff IDs. Upload class list → done." },
              { icon: Zap, title: "NGOs", desc: "Member and volunteer cards for events and drives." },
              { icon: IdCard, title: "Business Owners", desc: "Employee IDs, visitor passes, event badges." },
            ].map((s, i) => (
              <div key={s.title} className={cardCls}>
                <s.icon className="w-8 h-8 mb-4 text-[#002FA7]" strokeWidth={2}/>
                <h3 className="font-display font-bold text-2xl mb-2">{s.title}</h3>
                <p className="text-sm text-gray-700">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="border-t-2 border-black bg-white">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="mb-12 text-center">
            <div className="font-mono text-xs uppercase tracking-widest text-[#002FA7] font-bold mb-2">// Pricing</div>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Pay per card. No subscription.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className={cardCls}>
              <div className="font-mono text-xs uppercase tracking-widest text-gray-500 mb-2">Free Tier</div>
              <div className="font-display font-black text-6xl mb-4">₹0</div>
              <div className="text-sm text-gray-700 mb-6">First ID card free — try before you buy.</div>
              <ul className="space-y-2 text-sm">
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-[#22C55E] mt-0.5"/> 1 free HD ID card</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-[#22C55E] mt-0.5"/> Template upload</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-[#22C55E] mt-0.5"/> PNG + PDF export</li>
              </ul>
            </div>
            <div className="bg-black text-white brutal-border brutal-shadow-lg p-8">
              <div className="font-mono text-xs uppercase tracking-widest text-[#22C55E] mb-2">Pay As You Go</div>
              <div className="font-display font-black text-6xl mb-4">₹10<span className="text-2xl text-gray-400">/card</span></div>
              <div className="text-sm text-gray-300 mb-6">Bulk generate hundreds of cards. Pay only for what you use.</div>
              <ul className="space-y-2 text-sm">
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-[#22C55E] mt-0.5"/> Unlimited templates</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-[#22C55E] mt-0.5"/> Excel bulk upload</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-[#22C55E] mt-0.5"/> HD PNG + print-ready PDF</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-[#22C55E] mt-0.5"/> Credits never expire</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t-2 border-black bg-[#002FA7] text-white">
        <div className="max-w-7xl mx-auto px-6 py-24 text-center">
          <h2 className="font-display font-black text-5xl md:text-7xl tracking-tighter mb-6">Ready to forge?</h2>
          <p className="text-xl mb-8 opacity-90">Your first ID card is on us. Get started in 60 seconds.</p>
          <Link to="/register" className="inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-wider px-8 py-5 brutal-border brutal-shadow brutal-hover" data-testid="cta-bottom">
            Create My Free ID <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t-2 border-black bg-black text-white">
        <div className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 bg-white flex items-center justify-center">
                <IdCard className="w-5 h-5 text-black" strokeWidth={2.5}/>
              </div>
              <span className="font-display font-black text-xl tracking-tighter">DIGITAL CARD</span>
            </div>
            <p className="text-sm text-gray-400 max-w-xs">Bulk ID card generator for schools, NGOs and business owners. Made in India.</p>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-3">// Support</div>
            <div className="space-y-2">
              <a href="tel:+919911746606" className="flex items-center gap-2 text-sm hover:underline" data-testid="footer-helpline">
                <Phone className="w-4 h-4 text-[#22C55E]"/>
                <span>Helpline: <span className="font-bold">+91 99117 46606</span></span>
              </a>
              <a href="mailto:digitalconsultancydelhi@gmail.com" className="flex items-center gap-2 text-sm hover:underline break-all" data-testid="footer-email">
                <Mail className="w-4 h-4 text-[#22C55E]"/>
                <span>digitalconsultancydelhi@gmail.com</span>
              </a>
            </div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-3">// Company</div>
            <div className="space-y-2 text-sm">
              <div>Digital Consultancy Delhi</div>
              <div className="text-gray-500 text-xs">Hours: Mon–Sat · 10:00–19:00 IST</div>
            </div>
          </div>
        </div>
        <div className="border-t border-gray-800">
          <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="font-mono text-xs uppercase tracking-widest">© 2026 DIGITAL CARD — Made for vendors</div>
            <div className="font-mono text-xs uppercase tracking-widest opacity-70">v1.0 · press-ready · rgb</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
